"use client";

import Image from "next/image";
import Link from "@/components/ui/Link";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type VideoItem = {
  title?: FieldConfig;
  slug?: string;
  imageSrc?: FieldConfig;
  timeAgo?: FieldConfig;
  authorName?: string;
  imageMetadata?: ImageMetadata;
};

type Props = {
  videos?: VideoItem[];
  imageMetadata?: ImageMetadata;
};

export default function HorizontalSmallVideoRightNews({ videos = [], imageMetadata }: Props) {
  // If no videos provided, show a single placeholder item
  if (videos.length === 0) {
    return (
      <article className="overflow-hidden p-4">
        <div className="flex flex-row-reverse gap-4">
          {/* RIGHT: VIDEO THUMBNAIL */}
          <div className="w-[120px] h-[85px] lg:w-[130px] lg:h-[100px] xl:w-[150px] xl:h-[112px] flex-shrink-0">
            <div className="relative w-full h-full overflow-hidden">
              <Image
                src={FALLBACK_IMAGE}
                alt="Default video thumbnail"
                fill
                className="object-contain w-full h-full"
                style={getFocusPointStyle(imageMetadata)}
                sizes="(min-width: 1024px) 200px, 100vw"
                loading="lazy"
                placeholder="blur"
                blurDataURL={BLUR_PLACEHOLDER}
              />
              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-000000/20 hover:bg-000000/30 transition-colors">
              </div>
            </div>
          </div>

          {/* LEFT: CONTENT */}
          <div className="flex flex-col justify-between gap-2 lg:min-h-[134px] flex-1 min-w-0">
            <div className="flex flex-col gap-2">
              {/* Category */}
              <div className="text-11-tamil-600 text-012061 tracking-wide uppercase">
                வீடியோக்கள்
              </div>

              {/* Title */}
              <h3 className="text-1B1B1B text-14-tamil-700 leading-[1.4] line-clamp-2">
                தினமணி வீடியோக்கள்
              </h3>

              {/* Excerpt */}
              <p className="text-4D4D4D text-12-english-400 line-clamp-2">
                சமீபத்திய வீடியோக்கள் விரைவில்...
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <span className="text-12-tamil-500 text-1B1B1B">இப்போது</span>
              <MessageIcon
                id="video-news-message-icon"
                category={BOOKMARK_CATEGORIES.VIDEOS}
                title="தினமணி வீடியோக்கள்"
                thumbnailUrl={FALLBACK_IMAGE}
              />
            </div>
          </div>
        </div>
      </article>
    );
  }

  // Render the first 4 videos
  return (
    <>
      {videos.slice(0, 4).map((video, index) => {
        const titleValue = video.title?.value || "";
        const imageSrcValue = getImageUrl(video.imageSrc?.value) || FALLBACK_IMAGE;
        const timeAgoValue = getTimeAgo(video.timeAgo?.value);

        return (
          <Link
            key={`${video.slug}-${index}`}
            href={`/${video.slug}`}
            className="block  transition-colors cursor-pointer"
          >
            <article className="overflow-hidden p-4 border-b border-DCDCDC">
              <div className="flex flex-row-reverse gap-4">
                {/* RIGHT: VIDEO THUMBNAIL */}
                {!video.imageSrc?.hidden && (
                  <div className={`w-[120px] h-[85px] lg:w-[130px] lg:h-[100px] xl:w-[150px] xl:h-[112px] flex-shrink-0 ${video.imageSrc?.customClass || ""}`}>
                    <div className="relative w-full h-full overflow-hidden">
                      <Image
                        src={imageSrcValue}
                        alt={titleValue}
                        fill
                        className="object-contain w-full h-full"
                        style={getFocusPointStyle(video.imageMetadata ?? imageMetadata)}
                        sizes="(min-width: 1024px) 200px, 100vw"
                        loading="lazy"
                        placeholder="blur"
                        blurDataURL={BLUR_PLACEHOLDER}
                      />
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-000000/20 hover:bg-000000/30 transition-colors">
                      </div>
                    </div>
                  </div>
                )}

                {/* LEFT: CONTENT */}
                <div className="flex flex-col justify-between gap-2 lg:min-h-[134px] flex-1 min-w-0">
                  <div className="flex flex-col gap-2">
                    {/* Category */}
                    <div className="text-11-tamil-600 text-012061 tracking-wide uppercase">
                      வீடியோக்கள்
                    </div>

                    {/* Title */}
                    {!video.title?.hidden && (
                      <h3 className={`text-1B1B1B text-14-tamil-700 md:text-12-tamil-600 leading-[1.4] line-clamp-2 ${video.title?.customClass || ""}`}>
                        {titleValue}
                      </h3>
                    )}

                    {/* Author Name */}
                    {video.authorName && (
                      <p className="text-4D4D4D text-12-tamil-400">
                        {video.authorName}
                      </p>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    {!video.timeAgo?.hidden && (
                      <span className={`text-12-tamil-500 text-1B1B1B ${video.timeAgo?.customClass || ""}`}>
                        {timeAgoValue}
                      </span>
                    )}
                    <MessageIcon
                      id={`video-news-message-icon-${index}`}
                      slug={video.slug}
                      category={BOOKMARK_CATEGORIES.VIDEOS}
                      title={titleValue}
                      thumbnailUrl={imageSrcValue}
                      articleUrl={`/${video.slug}`}
                    />
                  </div>
                </div>
              </div>
            </article>
          </Link>
        );
      })}
    </>
  );
}
